import React from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Alert, Space, Skeleton, Progress, Tooltip } from 'antd';
import {
    CodeOutlined,
    EyeOutlined,
    CopyOutlined,
    ExpandAltOutlined,
    ShrinkOutlined,
    CheckCircleOutlined,
    FileTextOutlined
} from '@ant-design/icons';
import { DataCrafterTemplateFormStore, DataCrafterScriptCompilationStore } from '../../stores';
import { TemplateSubmitOptions } from '../../types';
import './DataCrafterTemplateScript.less';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { InView } from 'react-intersection-observer';
import { ValidationModelSelectionDialog } from '..';

interface DataCrafterTemplateScriptProps {
    store: DataCrafterTemplateFormStore;
    scriptCompilationStore: DataCrafterScriptCompilationStore;
    handleSubmit: (options?: TemplateSubmitOptions) => Promise<boolean>;
    validateFormFields?: () => Promise<{ templateName: string; plannerModelId: string; coderModelId: string } | null>;
}

const DataCrafterTemplateScript = ({
    store,
    scriptCompilationStore,
    handleSubmit,
    validateFormFields
}: DataCrafterTemplateScriptProps) => {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [isCopied, setIsCopied] = React.useState(false);
    const [showValidationFloatingButton, setShowValidationFloatingButton] = React.useState(false);
    const [isValidationModelSelectionDialogVisible, setIsValidationModelSelectionDialogVisible] = React.useState(false);
    const [selectedValidationModelId, setSelectedValidationModelId] = React.useState<string | undefined>(undefined);

    const isCompiling =
        scriptCompilationStore.isCurrentTemplateLoading || scriptCompilationStore.isCurrentTemplatePreparingCompilation;

    const isBusyWithScriptOperation = store.isBusyWithScriptOperation;

    const runCompilationAction = async (action: () => Promise<void>) => {
        if (scriptCompilationStore.isBusyWithScriptOperation) {
            return;
        }

        const templateId = store.currentTemplateId;
        scriptCompilationStore.setIsPreparingCompilation(templateId, true);

        try {
            await action();
        } finally {
            scriptCompilationStore.setIsPreparingCompilation(templateId, false);
        }
    };

    const handleCompile = () =>
        runCompilationAction(async () => {
            // For create mode (new templates), validate form fields without creating template
            if (validateFormFields && !store.isEditMode) {
                const formValues = await validateFormFields();
                if (!formValues) {
                    return; // Form validation failed
                }

                // Update store values from form
                store.setTemplateName(formValues.templateName);
                store.setSelectedPlannerModelId(formValues.plannerModelId);
                store.setSelectedCoderModelId(formValues.coderModelId);

                // Compile script with temporary template ID
                await store.compileTemplateScriptWithProgress();
            } else {
                // Save settings without accepting a candidate before retrying compilation.
                const success = await handleSubmit({
                    stayOnCurrentPage: true,
                    hideSuccessMessage: true,
                    preserveIncrementalCandidates: true
                });

                if (!success) {
                    return;
                }

                await store.compileTemplateScriptIncrementallyWithProgress();
            }
        });

    const handlePreview = async () => {
        await store.runTemplateScript();
    };

    const handleValidate = async () => {
        setIsValidationModelSelectionDialogVisible(true);
    };

    const handleValidationModelSelection = (modelId: string) => {
        setIsValidationModelSelectionDialogVisible(false);
        setSelectedValidationModelId(modelId);
        store.validateTemplateScript(modelId);
    };

    const handleCopy = () => {
        if (store.displayTemplateScript) {
            navigator.clipboard.writeText(store.displayTemplateScript).then(() => {
                setIsCopied(true);
                setTimeout(() => setIsCopied(false), 2000);
            });
        }
    };

    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    const renderLoadingState = () => {
        if (!isCompiling) {
            return null;
        }

        if (store.currentCompilationProgress) {
            const { message } = store.currentCompilationProgress;

            return (
                <div className="script-loading">
                    <Progress percent={100} status="active" showInfo={false} />
                    <div className="compilation-message">
                        <p className="message">{message}</p>
                    </div>
                </div>
            );
        }

        return (
            <div className="script-loading">
                <Skeleton active />
            </div>
        );
    };

    const renderValidationStatus = () => {
        if (!store.codeValidationContext) {
            return null;
        }

        if (store.codeValidationContext.hasErrors()) {
            return (
                <Alert
                    className="validation-status"
                    message="Script has potential issues"
                    description={renderValidationIssues()}
                    type="error"
                    showIcon
                />
            );
        }

        if (store.codeValidationContext.hasWarnings()) {
            return (
                <Alert
                    className="validation-status"
                    message="Script has potential issues"
                    description={renderValidationIssues()}
                    type="warning"
                    showIcon
                />
            );
        }

        return <Alert className="validation-status" message="Script validation passed" type="success" showIcon />;
    };

    const renderValidationIssues = () => {
        if (!store.codeValidationContext) {
            return null;
        }

        const rulesWithIssues = store.codeValidationContext.fetchRulesWithIssues();
        if (rulesWithIssues.length === 0) {
            return null;
        }

        return (
            <InView onChange={inView => setShowValidationFloatingButton(!inView)}>
                <div id="data-crafter-template-script-validation-issues" className="validation-issues">
                    {rulesWithIssues.map(rule => (
                        <div key={rule.rule_path} className="validation-issue">
                            <div className="validation-issue-header" onClick={() => navigateToRule(rule.rule_path)}>
                                <span className="validation-issue-header-text">{rule.rule_path}</span>
                            </div>
                            <div className="validation-issue-body">
                                <span className="validation-issue-body-text">
                                    {rule.issues_found.map(issue => issue.description).join(', ')}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </InView>
        );
    };

    const renderValidationFloatingButton = () => {
        if (!showValidationFloatingButton) {
            return null;
        }

        return (
            <div className="validation-floating-button">
                <Tooltip title="Go to validation summary" placement="topLeft">
                    <Button
                        shape="circle"
                        icon={<FileTextOutlined />}
                        onClick={navigateToValidationSummary}
                        size="large"
                        type="primary"
                    />
                </Tooltip>
            </div>
        );
    };

    const navigateToValidationSummary = () => {
        const validationIssuesElement = document.getElementById('data-crafter-template-script-validation-issues');
        if (validationIssuesElement) {
            validationIssuesElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    const navigateToRule = (rulePath: string) => {
        const tab = rulePath.split(':')[0];

        if (store.outputColumnsActiveTab !== tab) {
            store.setOutputColumnsActiveTab(tab);
        }

        setTimeout(() => {
            const ruleTooltipElement = document.getElementById(`rule-validation-status-${rulePath}`);
            if (ruleTooltipElement) {
                ruleTooltipElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                const rowElement = ruleTooltipElement.closest('tr');
                if (rowElement) {
                    // Remove any existing highlight classes first
                    document.querySelectorAll('tr.highlight').forEach(el => {
                        el.classList.remove('highlight');
                    });

                    // Add highlight class to the current row
                    rowElement.classList.add('highlight');

                    // Remove the highlight class after animation completes
                    setTimeout(() => {
                        rowElement.classList.remove('highlight');
                    }, 2000);
                }
            }
        }, 100);
    };

    return (
        <div className="data-crafter-template-script">
            <div className="script-header">
                <div className="action-buttons">
                    <Space>
                        <Button
                            type="primary"
                            icon={<CodeOutlined />}
                            onClick={handleCompile}
                            loading={isCompiling}
                            disabled={!store.canCompileScript || isBusyWithScriptOperation}
                        >
                            Compile Script
                        </Button>

                        <Button
                            icon={<EyeOutlined />}
                            onClick={handlePreview}
                            loading={scriptCompilationStore.isPreviewLoading}
                            disabled={!store.canPreviewScript || isBusyWithScriptOperation}
                        >
                            Preview Template
                        </Button>
                        <Button
                            icon={<CheckCircleOutlined />}
                            onClick={handleValidate}
                            loading={scriptCompilationStore.isValidateLoading}
                            disabled={!store.canValidateScript || isBusyWithScriptOperation}
                        >
                            Validate Script
                        </Button>
                    </Space>
                </div>
                {store.displayTemplateScript && !isCompiling && (
                    <div className="utility-buttons">
                        <Space>
                            <Button
                                size="small"
                                icon={isExpanded ? <ShrinkOutlined /> : <ExpandAltOutlined />}
                                onClick={toggleExpand}
                            >
                                {isExpanded ? 'Collapse' : 'Expand'}
                            </Button>
                            <Button
                                size="small"
                                icon={<CopyOutlined />}
                                onClick={handleCopy}
                                type={isCopied ? 'text' : 'default'}
                            >
                                {isCopied ? 'Copied!' : 'Copy'}
                            </Button>
                        </Space>
                    </div>
                )}
            </div>
            {store.currentTemplateScriptError && (
                <Alert message={store.currentTemplateScriptError} type="error" showIcon className="script-error" />
            )}
            {renderValidationStatus()}
            {renderLoadingState()}
            {renderValidationFloatingButton()}
            <ValidationModelSelectionDialog
                isVisible={isValidationModelSelectionDialogVisible}
                setIsVisible={setIsValidationModelSelectionDialogVisible}
                mlModelList={store.mlModels}
                submitCallback={handleValidationModelSelection}
                modelId={selectedValidationModelId}
            />
            {!isCompiling && store.displayTemplateScript && (
                <div className={`script-container ${isExpanded ? 'expanded' : ''}`}>
                    <SyntaxHighlighter language="python" className="script-content">
                        {store.displayTemplateScript}
                    </SyntaxHighlighter>
                </div>
            )}
        </div>
    );
};

export default observer(DataCrafterTemplateScript);
